import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All requests use httpOnly cookies for auth (withCredentials: true)
const opts = { withCredentials: true };

const api = {
  // Leads
  getLeads: (params = {}) => axios.get(`${API}/leads`, { params, ...opts }),
  getLead: (id) => axios.get(`${API}/leads/${id}`, opts),
  createLead: (data) => axios.post(`${API}/leads`, data, opts),
  checkDuplicates: (data) => axios.post(`${API}/leads/check-duplicates`, data, opts),
  mergeLeads: (data) => axios.post(`${API}/leads/merge`, data, opts),
  scanDuplicates: () => axios.post(`${API}/leads/scan-duplicates`, {}, opts),
  updateLead: (id, data) => axios.put(`${API}/leads/${id}`, data, opts),
  deleteLead: (id) => axios.delete(`${API}/leads/${id}`, opts),
  addNote: (id, content) => axios.post(`${API}/leads/${id}/notes`, { content }, opts),
  shareLead: (id, user_ids, share_note = '') => axios.post(`${API}/leads/${id}/share`, { user_ids, share_note }, opts),
  snapLead: (id) => axios.post(`${API}/leads/${id}/snap`, {}, opts),
  unsnapLead: (id) => axios.post(`${API}/leads/${id}/unsnap`, {}, opts),

  // Bulk operations
  bulkUpdateLeads: (data) => axios.post(`${API}/leads/bulk/update`, data, opts),
  bulkDeleteLeads: (lead_ids) => axios.post(`${API}/leads/bulk/delete`, { lead_ids }, opts),
  bulkShareLeads: (lead_ids, user_ids) => axios.post(`${API}/leads/bulk/share`, { lead_ids, user_ids }, opts),

  // Custom fields
  getCustomFields: () => axios.get(`${API}/custom-fields`, opts),
  createCustomField: (data) => axios.post(`${API}/custom-fields`, data, opts),
  deleteCustomField: (id) => axios.delete(`${API}/custom-fields/${id}`, opts),

  // Groups
  getGroups: () => axios.get(`${API}/groups`, opts),
  getGroup: (id) => axios.get(`${API}/groups/${id}`, opts),
  getInvitableMembers: (id) => axios.get(`${API}/groups/${id}/invitable-members`, opts),

  // Group discovery + join requests
  discoverGroups: (params) => axios.get(`${API}/groups/discover`, { ...opts, params }),
  suggestedGroups: () => axios.get(`${API}/groups/discover/suggested`, opts),
  getCategoryMatchNotifs: () => axios.get(`${API}/account/category-match-notifications`, opts),
  setCategoryMatchNotifs: (enabled) => axios.put(`${API}/account/category-match-notifications`, { enabled }, opts),

  // Guest video analytics + SMS
  guestVideoAnalytics: (params) => axios.get(`${API}/video/guest-rooms/analytics/summary`, { ...opts, params }),
  sendGuestRoomSms: (roomId, phone, message) => axios.post(`${API}/video/guest-rooms/${roomId}/send-sms`, { phone, message }, opts),
  createJoinRequest: (groupId, message) => axios.post(`${API}/groups/${groupId}/join-requests`, { message }, opts),
  listJoinRequests: (groupId) => axios.get(`${API}/groups/${groupId}/join-requests`, opts),
  approveJoinRequest: (groupId, reqId) => axios.post(`${API}/groups/${groupId}/join-requests/${reqId}/approve`, {}, opts),
  denyJoinRequest: (groupId, reqId) => axios.post(`${API}/groups/${groupId}/join-requests/${reqId}/deny`, {}, opts),
  myJoinRequests: () => axios.get(`${API}/account/join-requests`, opts),
  createGroup: (data) => axios.post(`${API}/groups`, data, opts),
  updateGroup: (id, data) => axios.put(`${API}/groups/${id}`, data, opts),
  deleteGroup: (id) => axios.delete(`${API}/groups/${id}`, opts),
  addGroupMembers: (id, user_ids) => axios.post(`${API}/groups/${id}/members/add`, { user_ids }, opts),
  removeGroupMembers: (id, user_ids) => axios.post(`${API}/groups/${id}/members/remove`, { user_ids }, opts),
  shareLeadsToGroup: (id, lead_ids) => axios.post(`${API}/groups/${id}/share-leads`, { lead_ids }, opts),

  // Members
  getMembers: () => axios.get(`${API}/members`, opts),
  getUnassignedMembers: () => axios.get(`${API}/members/unassigned`, opts),
  assignMemberCreator: (userId, adminId) => axios.post(`${API}/members/${userId}/creator`, { admin_id: adminId }, opts),
  updateMemberRole: (id, role) => axios.put(`${API}/members/${id}/role`, { role }, opts),
  updateMemberProfile: (id, body) => axios.put(`${API}/members/${id}`, body, opts),
  uploadMemberAvatar: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/members/${id}/avatar`, fd, { ...opts, headers: { 'Content-Type': 'multipart/form-data' } });
  },
  impersonateMember: (id) => axios.post(`${API}/auth/impersonate/${id}`, {}, opts),
  endImpersonation: () => axios.post(`${API}/auth/impersonate/return`, {}, opts),
  nudgeMember: (id, message) => axios.post(`${API}/members/${id}/nudge`, { message }, opts),
  deleteMember: (id) => axios.delete(`${API}/members/${id}`, opts),
  adminCreateUser: (data) => axios.post(`${API}/admin/users`, data, opts),

  // Stats
  getStats: () => axios.get(`${API}/stats`, opts),
  getActivityFeed: () => axios.get(`${API}/activity-feed`, opts),

  // Tours
  getCompletedTours: () => axios.get(`${API}/account/tours`, opts),
  completeTour: (tourId) => axios.post(`${API}/account/tours/${tourId}/complete`, {}, opts),
  resetTours: () => axios.post(`${API}/account/tours/reset`, {}, opts),

  // Payments
  createCheckout: (origin_url, coupon_code = null) => axios.post(`${API}/payments/checkout`, { origin_url, coupon_code }, opts),
  getPaymentStatus: (session_id) => axios.get(`${API}/payments/status/${session_id}`, opts),
  getPaymentHistory: () => axios.get(`${API}/payments/history`, opts),

  // PayPal
  createPayPalOrder: (origin_url, coupon_code = null) => axios.post(`${API}/payments/paypal/create-order`, { origin_url, coupon_code }, opts),
  capturePayPalOrder: (order_id) => axios.post(`${API}/payments/paypal/capture-order/${order_id}`, {}, opts),

  // Cash App
  createCashAppCheckout: (origin_url, coupon_code = null) => axios.post(`${API}/payments/cashapp/checkout`, { origin_url, coupon_code }, opts),

  // Venmo
  createVenmoCheckout: (origin_url, coupon_code = null) => axios.post(`${API}/payments/venmo/checkout`, { origin_url, coupon_code }, opts),

  // Apple Pay
  createApplePayCheckout: (origin_url, coupon_code = null) => axios.post(`${API}/payments/applepay/checkout`, { origin_url, coupon_code }, opts),

  // Google Pay
  createGooglePayCheckout: (origin_url, coupon_code = null) => axios.post(`${API}/payments/googlepay/checkout`, { origin_url, coupon_code }, opts),

  // Payment Preference
  getPaymentPreference: () => axios.get(`${API}/account/payment-preference`, opts),
  setPaymentPreference: (method) => axios.put(`${API}/account/payment-preference`, { preferred_method: method }, opts),

  // Export
  exportLeadsCSV: (params = {}) => axios.get(`${API}/leads/export/csv`, { params, ...opts, responseType: 'blob' }),

  // Appointments
  getAppointments: (params = {}) => axios.get(`${API}/appointments`, { params, ...opts }),
  getAppointment: (id) => axios.get(`${API}/appointments/${id}`, opts),
  createAppointment: (data) => axios.post(`${API}/appointments`, data, opts),
  updateAppointment: (id, data) => axios.put(`${API}/appointments/${id}`, data, opts),
  deleteAppointment: (id) => axios.delete(`${API}/appointments/${id}`, opts),
  sendLeadReminder: (apptId, message = '') => axios.post(`${API}/appointments/${apptId}/send-lead-reminder`, { message }, opts),

  // Avatar
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/members/avatar`, formData, { ...opts, headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getAvatarUrl: (userId) => `${API}/members/avatar/${userId}`,

  // Account
  updateProfile: (data) => axios.put(`${API}/account/profile`, data, opts),
  updateBookingLink: (link) => axios.put(`${API}/account/booking-link`, { booking_link: link }, opts),
  getAutoReply: () => axios.get(`${API}/account/auto-reply`, opts),
  updateAutoReply: (data) => axios.put(`${API}/account/auto-reply`, data, opts),
  changePassword: (data) => axios.put(`${API}/account/password`, data, opts),
  deleteAccount: (password) => axios.delete(`${API}/account`, { data: { password }, ...opts }),

  // Sessions
  getSessions: () => axios.get(`${API}/account/sessions`, opts),
  revokeSession: (sessionId) => axios.delete(`${API}/account/sessions/${sessionId}`, opts),
  revokeAllOtherSessions: () => axios.delete(`${API}/account/sessions`, opts),

  // Login Alerts
  getLoginAlerts: () => axios.get(`${API}/account/login-alerts`, opts),
  setLoginAlerts: (enabled) => axios.put(`${API}/account/login-alerts`, { login_alerts_enabled: enabled }, opts),

  // Forgot / Reset Password
  forgotPassword: (email) => axios.post(`${API}/auth/forgot-password`, { email }, opts),
  requestPasswordReset: () => axios.post(`${API}/auth/request-password-reset`, {}, opts),
  adminSendPasswordReset: (memberId) => axios.post(`${API}/admin/members/${memberId}/send-password-reset`, {}, opts),
  resetPassword: (token, new_password) => axios.post(`${API}/auth/reset-password`, { token, new_password }, opts),

  // Email Verification
  sendVerification: (data) => axios.post(`${API}/auth/send-verification`, data, opts),
  verifyEmail: (email, code) => axios.post(`${API}/auth/verify-email`, { email, code }, opts),

  // Organization
  getOrganization: () => axios.get(`${API}/organization`, opts),
  createOrganization: (name) => axios.post(`${API}/organization`, { name }, opts),
  updateOrganization: (data) => axios.put(`${API}/organization`, data, opts),
  inviteToOrganization: (data) => axios.post(`${API}/organization/invite`, data, opts),
  removeFromOrganization: (memberId) => axios.delete(`${API}/organization/members/${memberId}`, opts),
  switchToIndividual: () => axios.post(`${API}/organization/switch-to-individual`, {}, opts),
  bulkInviteToOrganization: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/organization/bulk-invite`, formData, { ...opts, headers: { 'Content-Type': 'multipart/form-data' } });
  },
  downloadBulkInviteTemplate: () => axios.get(`${API}/organization/bulk-invite/template`, { ...opts, responseType: 'blob' }),

  // Digest Preference
  getDigestPreference: () => axios.get(`${API}/account/digest-preference`, opts),
  setDigestPreference: (pref) => axios.put(`${API}/account/digest-preference`, { digest_preference: pref }, opts),
  setPerformanceDigest: (enabled) => axios.put(`${API}/account/performance-digest`, { enabled }, opts),

  // CSV Import
  importLeadsCSV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/leads/import/csv`, formData, { ...opts, headers: { 'Content-Type': 'multipart/form-data' } });
  },

  // Notifications
  getNotifications: () => axios.get(`${API}/notifications`, opts),
  getNotificationCount: () => axios.get(`${API}/notifications/count`, opts),
  markAllRead: () => axios.post(`${API}/notifications/mark-read`, {}, opts),
  markOneRead: (id) => axios.post(`${API}/notifications/${id}/read`, {}, opts),

  // Onboarding
  completeOnboarding: () => axios.post(`${API}/onboarding/complete`, {}, opts),
  getOnboardingStatus: () => axios.get(`${API}/onboarding/status`, opts),

  // Admin Analytics
  getAdminAnalytics: () => axios.get(`${API}/admin/analytics`, opts),
  getMemberAnalytics: () => axios.get(`${API}/admin/member-analytics`, opts),
  getMyAnalytics: () => axios.get(`${API}/analytics/my`, opts),
  getOrgLeaderboard: () => axios.get(`${API}/leaderboard`, opts),

  // Seamless.AI
  seamlessSearchContacts: (body) => axios.post(`${API}/seamless/search/contacts`, body, opts),
  seamlessSearchCompanies: (body) => axios.post(`${API}/seamless/search/companies`, body, opts),
  seamlessResearchContacts: (body) => axios.post(`${API}/seamless/research/contacts`, body, opts),
  seamlessPollContactResearch: (ids) => axios.get(`${API}/seamless/research/contacts/poll?requestIds=${ids}`, opts),
  seamlessImport: (contacts) => axios.post(`${API}/seamless/import`, { contacts }, opts),

  // Saved filters (Super-Admin only)
  listSeamlessSavedFilters: () => axios.get(`${API}/seamless/saved-filters`, opts),
  createSeamlessSavedFilter: (name, filters) => axios.post(`${API}/seamless/saved-filters`, { name, filters }, opts),
  deleteSeamlessSavedFilter: (id) => axios.delete(`${API}/seamless/saved-filters/${id}`, opts),
  seamlessEnrichLead: (leadId) => axios.post(`${API}/seamless/enrich-lead/${leadId}`, {}, opts),
  seamlessApplyEnrichment: (leadId, contact) => axios.post(`${API}/seamless/enrich-lead/${leadId}/apply`, { contact }, opts),
  seamlessSyncEnrich: (leadId) => axios.post(`${API}/seamless/sync-enrich/${leadId}`, {}, opts),
  bulkEnrichSeamless: (leadIds) => axios.post(`${API}/leads/bulk/enrich-seamless`, { lead_ids: leadIds }, opts),

  // Hunter.io
  hunterFindForLead: (leadId) => axios.post(`${API}/hunter/find-for-lead/${leadId}`, {}, opts),
  hunterVerifyForLead: (leadId) => axios.post(`${API}/hunter/verify-for-lead/${leadId}`, {}, opts),
  hunterDomainSearch: (payload) => axios.post(`${API}/hunter/domain-search`, payload, opts),
  hunterEmailFinder: (payload) => axios.post(`${API}/hunter/email-finder`, payload, opts),
  hunterEmailVerifier: (email) => axios.post(`${API}/hunter/email-verifier`, { email }, opts),
  hunterBulkFind: (leadIds) => axios.post(`${API}/hunter/bulk-find`, { lead_ids: leadIds }, opts),
  hunterBulkDomainSearch: (domains, limitPerDomain = 10) => axios.post(`${API}/hunter/bulk-domain-search`, { domains, limit_per_domain: limitPerDomain }, opts),
  hunterBulkImportContacts: (contacts, autoVerify = false) => axios.post(`${API}/hunter/bulk-import-contacts`, { contacts, auto_verify: autoVerify }, opts),
  hunterUsage: (scope = 'me', period = 'month') => axios.get(`${API}/hunter/usage`, { params: { scope, period }, ...opts }),
  hunterAccount: () => axios.get(`${API}/hunter/account`, opts),

  // Maintenance
  runMaintenance: (options = {}) => axios.post(`${API}/maintenance/run`, {}, {
    ...opts,
    params: { dry_run: options.dryRun ? 'true' : 'false', notify: options.notify === false ? 'false' : 'true' },
  }),
  maintenanceLast: () => axios.get(`${API}/maintenance/last`, opts),
  maintenanceReports: (limit = 10) => axios.get(`${API}/maintenance/reports`, { params: { limit }, ...opts }),
  maintenanceBackups: () => axios.get(`${API}/maintenance/backups`, opts),
  maintenanceCreateBackup: () => axios.post(`${API}/maintenance/backup`, {}, opts),
  maintenanceRestore: (filename, confirm) => axios.post(`${API}/maintenance/restore`, { filename, confirm }, opts),
  maintenanceDashboard: () => axios.get(`${API}/maintenance/dashboard`, opts),

  // Global search (nav bar cmd+K)
  search: (q, limit) => axios.get(`${API}/search`, { ...opts, params: { q, limit } }),

  // Lead handoff (share + auto-DM in one call)
  leadHandoff: (leadId, expertId, note) =>
    axios.post(`${API}/leads/${leadId}/handoff`, { expert_id: expertId, note: note || '' }, opts),

  // Auto-nudge preference for stuck handoffs
  getHandoffNudgePref: () => axios.get(`${API}/account/handoff-nudge-preference`, opts),
  setHandoffNudgePref: (enabled) =>
    axios.put(`${API}/account/handoff-nudge-preference`, { auto_nudge_handoffs: !!enabled }, opts),

  // Team collaboration analytics (admin)
  collaborationAnalytics: (days) =>
    axios.get(`${API}/admin/collaboration-analytics`, { ...opts, params: { days } }),

  // Version / build info (public)
  version: () => axios.get(`${API}/version`),

  // Payment fee preview (Stripe surcharge)
  feeQuote: (amount) => axios.get(`${API}/payments/fee-quote`, { ...opts, params: { amount } }),

  // Billing email (separate invoice recipient — e.g. AP forwarder alias)
  getBillingEmail: () => axios.get(`${API}/account/billing-email`, opts),
  setBillingEmail: (billing_email) => axios.put(`${API}/account/billing-email`, { billing_email }, opts),

  // Release/commit changelog (admin)
  changelog: (limit) => axios.get(`${API}/admin/changelog`, { ...opts, params: { limit } }),

  // Marketing videos (Sora 2, admin only)
  marketingVideoTemplates: () => axios.get(`${API}/marketing/video-templates`, opts),
  marketingVideoList: () => axios.get(`${API}/marketing/videos`, opts),
  marketingVideoGet: (id) => axios.get(`${API}/marketing/videos/${id}`, opts),
  marketingVideoCreate: (payload) => axios.post(`${API}/marketing/videos`, payload, opts),
  marketingVideoDelete: (id) => axios.delete(`${API}/marketing/videos/${id}`, opts),
  marketingVideoDownloadUrl: (id) => `${API}/marketing/videos/${id}/download`,

  // Lead Requests workflow
  listLeadRequests: (params = {}) => {
    const qs = typeof params === 'string'
      ? (params ? `?status=${params}` : '')
      : '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== '')).toString();
    return axios.get(`${API}/lead-requests${qs.length > 1 ? qs : ''}`, opts);
  },
  createLeadRequest: (payload) => axios.post(`${API}/lead-requests`, payload, opts),
  updateLeadRequest: (id, updates) => axios.patch(`${API}/lead-requests/${id}`, updates, opts),
  deleteLeadRequest: (id) => axios.delete(`${API}/lead-requests/${id}`, opts),
  autoFulfillLeadRequest: (id) => axios.post(`${API}/lead-requests/${id}/auto-fulfill`, {}, opts),
  rateLeadRequest: (id, rating, comment = '') => axios.post(`${API}/lead-requests/${id}/rate`, { rating, comment }, opts),
  getSuperAdminPerformance: (days = 'all') => axios.get(`${API}/lead-requests/performance?days=${days}`, opts),
  seamlessStatus: () => axios.get(`${API}/seamless/status`, opts),

  // Assignment Rules
  getAssignmentRules: () => axios.get(`${API}/assignment-rules`, opts),
  createAssignmentRule: (data) => axios.post(`${API}/assignment-rules`, data, opts),
  updateAssignmentRule: (id, data) => axios.put(`${API}/assignment-rules/${id}`, data, opts),
  deleteAssignmentRule: (id) => axios.delete(`${API}/assignment-rules/${id}`, opts),

  // Annual billing
  createAnnualCheckout: (origin_url, payment_method = 'card', coupon_code = null) => axios.post(`${API}/payments/annual/checkout`, { origin_url, payment_method, coupon_code }, opts),
  createSeatsCheckout: (seats, billingCycle, originUrl) => axios.post(`${API}/payments/seats/checkout`, { seats, billing_cycle: billingCycle, origin_url: originUrl }, opts),

  // Zelle
  getZelleInfo: () => axios.get(`${API}/payments/zelle/info`, opts),
  createZelleRequest: (data) => axios.post(`${API}/payments/zelle/request`, data, opts),
  approveZelle: (ref) => axios.post(`${API}/payments/zelle/approve/${ref}`, {}, opts),

  // Coupons
  getActiveCoupons: () => axios.get(`${API}/coupons/active`, opts),
  validateCoupon: (code, plan) => axios.post(`${API}/coupons/validate`, { code, plan }, opts),

  // Admin: Coupons
  adminListCoupons: () => axios.get(`${API}/admin/coupons`, opts),
  adminCreateCoupon: (data) => axios.post(`${API}/admin/coupons`, data, opts),
  adminUpdateCoupon: (code, data) => axios.put(`${API}/admin/coupons/${code}`, data, opts),
  adminDeleteCoupon: (code) => axios.delete(`${API}/admin/coupons/${code}`, opts),

  // Admin: SMS Campaign Submission Pack (A2P 10DLC)
  getSmsCampaign: () => axios.get(`${API}/admin/sms-campaign`, opts),
  updateSmsCampaign: (data) => axios.put(`${API}/admin/sms-campaign`, data, opts),

  // Admin: SMS Opt-In Audit Log (A2P 10DLC)
  getSmsConsentLog: (params = {}) => axios.get(`${API}/admin/sms-consent-log`, { ...opts, params }),
  exportSmsConsentLogCSV: () =>
    axios.get(`${API}/admin/sms-consent-log/export`, { ...opts, responseType: 'blob' }),

  // Admin: Monthly SMS Compliance Report (A2P 10DLC)
  previewSmsComplianceReport: () => axios.get(`${API}/admin/sms-compliance-report/preview`, opts),
  sendSmsComplianceReportNow: () =>
    axios.post(`${API}/admin/sms-compliance-report/send-now`, {}, opts),
  resendSmsConsentConfirmation: (eventId) =>
    axios.post(`${API}/admin/sms-consent-log/${encodeURIComponent(eventId)}/resend-confirmation`, {}, opts),
  getComplianceHealth: () => axios.get(`${API}/admin/compliance-health`, opts),
  getComplianceHealthHistory: (days = 30) =>
    axios.get(`${API}/admin/compliance-health/history`, { ...opts, params: { days } }),

  // What's New (changelog modal)
  getWhatsNew: () => axios.get(`${API}/whats-new`, opts),
  dismissWhatsNew: () => axios.post(`${API}/whats-new/dismiss`, {}, opts),

  // Marketing Email: Trial-Signup Template
  getMarketingEmailTrialSignup: (name = '', variant = 'loss_aversion') =>
    axios.get(`${API}/admin/marketing-email/trial-signup`, { ...opts, params: { name, variant } }),
  sendMarketingEmailTestTrialSignup: () =>
    axios.post(`${API}/admin/marketing-email/trial-signup/send-test`, {}, opts),
  sendMarketingEmail: (recipients, variant = 'loss_aversion') =>
    axios.post(`${API}/admin/marketing-email/trial-signup/send`, { recipients, variant }, opts),
  listMarketingEmailSends: (limit = 100) =>
    axios.get(`${API}/admin/marketing-email/sends`, { ...opts, params: { limit } }),
  countNonOpeners: (variant) =>
    axios.get(`${API}/admin/marketing-email/non-openers/${variant}/count`, opts),
  resendNonOpeners: (originalVariant, fallbackVariant = null) =>
    axios.post(
      `${API}/admin/marketing-email/trial-signup/resend-non-openers`,
      fallbackVariant
        ? { original_variant: originalVariant, fallback_variant: fallbackVariant }
        : { original_variant: originalVariant },
      opts,
    ),
  getMarketingDripSettings: () => axios.get(`${API}/admin/marketing-drip/settings`, opts),
  saveMarketingDripSettings: (data) => axios.put(`${API}/admin/marketing-drip/settings`, data, opts),
  runMarketingDripNow: () => axios.post(`${API}/admin/marketing-drip/run-now`, {}, opts),
  getMarketingDripFunnel: () => axios.get(`${API}/admin/marketing-drip/funnel`, opts),
  exportMarketingDripCsv: () => axios.get(`${API}/admin/marketing-drip/export`, { ...opts, responseType: 'text' }),
  getSavedQuotes: (params = {}) => axios.get(`${API}/admin/saved-quotes`, { params, ...opts }),
  nudgeSavedQuote: (email) => axios.post(`${API}/admin/saved-quotes/nudge`, { email }, opts),
  getTurnStatus: () => axios.get(`${API}/admin/turn-status`, opts),

  sendTypingIndicator: (recipient_id) => axios.post(`${API}/messages/typing`, { recipient_id }, opts),
  uploadMessageFile: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/messages/upload`, fd, { ...opts, headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getMessageAttachmentUrl: (messageId) => `${API}/messages/attachment/${messageId}`,
  toggleMessageReaction: (messageId, emoji) => axios.post(`${API}/messages/${messageId}/reactions`, { emoji }, opts),

  // WebRTC video/audio calls
  sendCallSignal: (recipient_id, call_id, type, payload = null) =>
    axios.post(`${API}/calls/signal`, { recipient_id, call_id, type, payload }, opts),
  getIceServers: () => axios.get(`${API}/calls/ice-servers`, opts),
  getRecentCalls: (limit = 5) => axios.get(`${API}/calls/recent`, { params: { limit }, ...opts }),
  submitCallStats: (callId, stats) => axios.post(`${API}/calls/${callId}/stats`, stats, opts),
  previewCallDigest: () => axios.get(`${API}/calls/digest/preview`, opts),
  sendCallDigestNow: () => axios.post(`${API}/calls/digest/send-now`, {}, opts),
  // Online members (presence)
  getOnlineMembers: () => axios.get(`${API}/members/online`, opts),

  // Subscriptions (recurring billing on 1st of month)
  prorationPreview: (seats, cycle) => axios.post(`${API}/payments/proration-preview`, { seats, cycle }, opts),
  createSubscriptionCheckout: (origin_url, seats, cycle, coupon_code = null) =>
    axios.post(`${API}/payments/subscribe`, { origin_url, seats, cycle, coupon_code }, opts),
  removeSeat: (memberId) => axios.post(`${API}/payments/seats/remove-from-subscription`, { member_id: memberId }, opts),
  addSeatsToSubscription: (additional_seats) =>
    axios.post(`${API}/payments/seats/add-to-subscription`, { additional_seats }, opts),

  // Admin export
  exportPaymentsCSV: () => axios.get(`${API}/admin/payments/export`, { ...opts, responseType: 'blob' }),

  // Messages
  getConversations: () => axios.get(`${API}/messages/conversations`, opts),

  // Documents
  uploadDocument: (leadId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/leads/${leadId}/documents`, formData, { ...opts, headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getDocuments: (leadId) => axios.get(`${API}/leads/${leadId}/documents`, opts),
  downloadDocument: (leadId, docId) => axios.get(`${API}/leads/${leadId}/documents/${docId}/download`, { ...opts, responseType: 'blob' }),
  deleteDocument: (leadId, docId) => axios.delete(`${API}/leads/${leadId}/documents/${docId}`, opts),
  getMessages: (partnerId) => axios.get(`${API}/messages/${partnerId}`, opts),
  sendMessage: (recipient_id, content, attachment = null) => axios.post(`${API}/messages`, { recipient_id, content, attachment }, opts),
  getUnreadMessageCount: () => axios.get(`${API}/messages/unread/count`, opts),

  // CRM Import
  previewCRMImport: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/leads/import/crm/preview`, formData, { ...opts, headers: { 'Content-Type': 'multipart/form-data' } });
  },
  importCRMLeads: (data) => axios.post(`${API}/leads/import/crm`, data, opts),

  // Saved Filters
  getSavedFilters: (scope) => axios.get(`${API}/saved-filters`, { ...opts, params: scope ? { scope } : {} }),
  createSavedFilter: (data) => axios.post(`${API}/saved-filters`, data, opts),
  deleteSavedFilter: (id) => axios.delete(`${API}/saved-filters/${id}`, opts),

  // Webhooks
  getWebhooks: () => axios.get(`${API}/webhooks`, opts),
  createWebhook: (data) => axios.post(`${API}/webhooks`, data, opts),
  updateWebhook: (id, data) => axios.put(`${API}/webhooks/${id}`, data, opts),
  deleteWebhook: (id) => axios.delete(`${API}/webhooks/${id}`, opts),
  getWebhookLogs: (id) => axios.get(`${API}/webhooks/${id}/logs`, opts),

  // Email Campaigns
  getCampaigns: () => axios.get(`${API}/campaigns`, opts),
  createCampaign: (data) => axios.post(`${API}/campaigns`, data, opts),
  updateCampaign: (id, data) => axios.put(`${API}/campaigns/${id}`, data, opts),
  deleteCampaign: (id) => axios.delete(`${API}/campaigns/${id}`, opts),
  enrollLeads: (campId, leadIds) => axios.post(`${API}/campaigns/${campId}/enroll`, { lead_ids: leadIds }, opts),
  getEnrollments: (campId) => axios.get(`${API}/campaigns/${campId}/enrollments`, opts),
  removeEnrollment: (campId, enrollId) => axios.delete(`${API}/campaigns/${campId}/enrollments/${enrollId}`, opts),

  // Support Chat
  getSupportChats: () => axios.get(`${API}/support/chats`, opts),
  createSupportChat: () => axios.post(`${API}/support/chats`, {}, opts),
  getSupportMessages: (chatId) => axios.get(`${API}/support/chats/${chatId}/messages`, opts),
  sendSupportMessage: (chatId, content, attachmentPath) => axios.post(`${API}/support/chats/${chatId}/messages`, { content, attachment_path: attachmentPath || null }, opts),
  uploadSupportAttachment: (chatId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/support/chats/${chatId}/upload`, formData, { ...opts, headers: { 'Content-Type': 'multipart/form-data' } });
  },
  escalateChat: (chatId) => axios.put(`${API}/support/chats/${chatId}/escalate`, {}, opts),
  resolveChat: (chatId) => axios.put(`${API}/support/chats/${chatId}/resolve`, {}, opts),
  getCannedResponses: () => axios.get(`${API}/support/canned-responses`, opts),

  // NPS Feedback
  submitFeedback: (chatId, score, comment) => axios.post(`${API}/support/feedback`, { chat_id: chatId, score, comment }, opts),
  getFeedbackAnalytics: () => axios.get(`${API}/support/feedback/analytics`, opts),

  // Manual PDF
  downloadManualPDF: () => axios.get(`${API}/manual/pdf`, { ...opts, responseType: 'blob' }),

  // A/B Testing
  trackABEvent: (test, variant, event) => axios.post(`${API}/ab/track`, { test, variant, event }),
  getABResults: () => axios.get(`${API}/admin/ab/results`, opts),

  // Scratchpad
  getScratchpad: () => axios.get(`${API}/scratchpad`, opts),
  saveScratchpad: (content) => axios.put(`${API}/scratchpad`, { content }, opts),

  // Theme
  getTheme: () => axios.get(`${API}/auth/theme`, opts),
  saveTheme: (prefs) => axios.put(`${API}/auth/theme`, prefs, opts),

  // Email Integration
  getEmailAccounts: () => axios.get(`${API}/email/accounts`, opts),
  addEmailAccount: (data) => axios.post(`${API}/email/accounts`, data, opts),
  disconnectEmailAccount: (id) => axios.delete(`${API}/email/accounts/${id}`, opts),
  checkEmailHealth: () => axios.get(`${API}/email/accounts/health`, opts),
  sendEmailViaProvider: (payload) => axios.post(`${API}/email/send`, payload, opts),
  getLeadEmailHistory: (leadId) => axios.get(`${API}/email/lead/${leadId}`, opts),
  getSentEmails: (limit = 50, skip = 0) => axios.get(`${API}/email/sent?limit=${limit}&skip=${skip}`, opts),
  getEmailTracking: (emailId) => axios.get(`${API}/email/tracking/${emailId}`, opts),
  // Email Sequences
  getEmailSequences: () => axios.get(`${API}/email/sequences`, opts),
  createEmailSequence: (data) => axios.post(`${API}/email/sequences`, data, opts),
  updateEmailSequence: (id, data) => axios.put(`${API}/email/sequences/${id}`, data, opts),
  deleteEmailSequence: (id) => axios.delete(`${API}/email/sequences/${id}`, opts),
  enrollInSequence: (seqId, leadIds) => axios.post(`${API}/email/sequences/${seqId}/enroll`, { lead_ids: leadIds }, opts),
  getSequenceEnrollments: (seqId) => axios.get(`${API}/email/sequences/${seqId}/enrollments`, opts),
  removeSequenceEnrollment: (seqId, enrollId) => axios.delete(`${API}/email/sequences/${seqId}/enrollments/${enrollId}`, opts),
  getSequenceAnalytics: (seqId) => axios.get(`${API}/email/sequences/${seqId}/analytics`, opts),
  // Reports
  getLeadsAgingReport: (params = {}) => axios.get(`${API}/reports/leads-aging`, { params, ...opts }),
  getConversionFunnel: (params = {}) => axios.get(`${API}/reports/conversion-funnel`, { params, ...opts }),
  getEmailAnalytics: (params = {}) => axios.get(`${API}/email/analytics`, { params, ...opts }),
  // Categories
  getCategories: () => axios.get(`${API}/categories`, opts),
  addCategory: (data) => axios.post(`${API}/categories/add`, data, opts),
  updateCategory: (name, data) => axios.put(`${API}/categories/${encodeURIComponent(name)}`, data, opts),
  deleteCategory: (name) => axios.delete(`${API}/categories/${encodeURIComponent(name)}`, opts),
  getCategoryPreferences: () => axios.get(`${API}/category-preferences`, opts),
  setCategoryPreferences: (prefs) => axios.put(`${API}/category-preferences`, { preferred_categories: prefs }, opts),
  // Module Permissions
  getMemberPermissions: (userId) => axios.get(`${API}/members/${userId}/permissions`, opts),
  setMemberPermissions: (userId, modules) => axios.put(`${API}/members/${userId}/permissions`, { module_permissions: modules }, opts),

  // Audit Log
  getAuditLog: (params = {}) => axios.get(`${API}/audit-log`, { ...opts, params }),
  getAuditActions: () => axios.get(`${API}/audit-log/actions`, opts),

  // Code Quality
  runCodeQualityScan: () => axios.post(`${API}/code-quality/scan`, {}, opts),
  getLastCodeQualityScan: () => axios.get(`${API}/code-quality/last`, opts),
  getCodeQualityHistory: (limit = 10) => axios.get(`${API}/code-quality/history?limit=${limit}`, opts),
  getCodeQualitySummary: () => axios.get(`${API}/code-quality/summary`, opts),
  getCodeQualityTrend: (days = 7) => axios.get(`${API}/code-quality/trend?days=${days}`, opts),

  // Web Push
  getPushPublicKey: () => axios.get(`${API}/push/public-key`, opts),
  subscribePush: (subscription) => axios.post(`${API}/push/subscribe`, subscription, opts),
  unsubscribePush: (endpoint) => axios.post(`${API}/push/unsubscribe`, { endpoint }, opts),

  // Voice transcription + AI analysis
  transcribeVoice: (blob, { language } = {}) => {
    const fd = new FormData();
    fd.append('audio', blob, 'voice.webm');
    const qs = language ? `?language=${encodeURIComponent(language)}` : '';
    return axios.post(`${API}/voice/transcribe${qs}`, fd, {
      ...opts,
      headers: { ...(opts.headers || {}), 'Content-Type': 'multipart/form-data' },
    });
  },
  analyzeTranscript: (text, context = null) =>
    axios.post(`${API}/voice/analyze`, { text, context }, opts),

  // Membership Management
  updateMembership: (userId, status) => axios.put(`${API}/members/${userId}/membership`, { membership_status: status }, opts),

  getInbox: (accountId, limit = 30) => axios.get(`${API}/email/inbox/${accountId}?limit=${limit}`, opts),
  getEmailMessage: (accountId, uid) => axios.get(`${API}/email/inbox/${accountId}/message/${uid}`, opts),
  getEmailTemplates: () => axios.get(`${API}/email/templates`, opts),
  createEmailTemplate: (data) => axios.post(`${API}/email/templates`, data, opts),
  updateEmailTemplate: (id, data) => axios.put(`${API}/email/templates/${id}`, data, opts),
  deleteEmailTemplate: (id) => axios.delete(`${API}/email/templates/${id}`, opts),

  // ClickSend SMS / MMS
  smsStatus: () => axios.get(`${API}/sms/status`, opts),
  smsBalance: () => axios.get(`${API}/sms/balance`, opts),
  sendSmsToLead: (leadId, body, mediaUrl = null) =>
    axios.post(`${API}/sms/send/${leadId}`, { body, media_url: mediaUrl }, opts),
  logLeadTouch: (leadId, kind) =>
    axios.post(`${API}/leads/${leadId}/log-touch`, { kind }, opts).catch(() => {}),
  sendBulkSms: (leadIds, body, mediaUrl = null) =>
    axios.post(`${API}/sms/bulk`, { lead_ids: leadIds, body, media_url: mediaUrl }, opts),
  getLeadSmsHistory: (leadId) => axios.get(`${API}/sms/history/${leadId}`, opts),
  getSmsInbox: () => axios.get(`${API}/sms/inbox`, opts),
  markSmsConversationRead: (leadId) => axios.post(`${API}/sms/inbox/${leadId}/read`, {}, opts),
  getSmsTemplates: () => axios.get(`${API}/sms/templates`, opts),
  suggestSmsTemplates: (leadId) => axios.get(`${API}/sms/templates/suggest`, { ...opts, params: { lead_id: leadId } }),
  createSmsTemplate: (data) => axios.post(`${API}/sms/templates`, data, opts),
  updateSmsTemplate: (id, data) => axios.put(`${API}/sms/templates/${id}`, data, opts),
  deleteSmsTemplate: (id) => axios.delete(`${API}/sms/templates/${id}`, opts),
  getGlobalSmsHistory: (params = {}) => axios.get(`${API}/sms/history`, { params, ...opts }),
  getSmsDrip: () => axios.get(`${API}/sms/drip`, opts),
  saveSmsDrip: (cfg) => axios.put(`${API}/sms/drip`, cfg, opts),
  smsConsentStatus: () => axios.get(`${API}/sms-consent/my-status`, opts),
  smsConsentRespond: (decision) =>
    axios.post(`${API}/sms-consent/respond`, { decision }, opts),
  smsConsentCoverage: () => axios.get(`${API}/admin/sms-consent-coverage`, opts),
  smsConsentCoverageTrend: (days = 30) =>
    axios.get(`${API}/admin/sms-consent-coverage/trend`, { params: { days }, ...opts }),
  smsConsentCoverageTrendCsv: (days = 90) =>
    axios.get(`${API}/admin/sms-consent-coverage/trend.csv`, { params: { days }, responseType: 'blob', ...opts }),
  sendFaceTimeInvite: (leadId, data) =>
    axios.post(`${API}/leads/${leadId}/facetime-invite`, data, opts),
  smsConsentInviteMissing: () =>
    axios.post(`${API}/admin/sms-consent/invite-missing`, {}, opts),
  turnStatus: () => axios.get(`${API}/admin/turn-status`, opts),

  // FaceTime public page
  getMyFaceTimePage: () => axios.get(`${API}/facetime/me`, opts),
  saveMyFaceTimePage: (cfg) => axios.put(`${API}/facetime/me`, cfg, opts),
  getPublicFaceTimePage: (slug) => axios.get(`${API}/facetime/${slug}`),
};

export default api;
